import { useCallback } from 'react'
import { useAthleteOptional } from '@/contexts/AthleteContext'
import type { Routine } from '@/services/api/routines'

export interface RoutineScope {
  /** The viewer owns it: Run now and the run's conversation are theirs. */
  own: boolean
  /** What to say under the name; null for the owner's own data, the common case. */
  label: string | null
}

/**
 * Per-row ownership and what each routine watches, for a list fetched against
 * `targetProfileId` (null = the viewer's own list).
 *
 * Against an athlete the list mixes the viewer's routines about them with the
 * athlete's OWN routines — mostly ones a coach made before routines had a
 * subject, which ran as the athlete. Those must read as the athlete's, or a
 * coach deletes the wrong row with one click.
 */
export function useRoutineScope(targetProfileId: number | null | undefined) {
  const athletes = useAthleteOptional()?.athletes
  return useCallback((routine: Routine): RoutineScope => {
    const known = athletes ?? []
    const nameOf = (id: number | null) => known.find((a) => a.profile_id === id)?.name
    if (targetProfileId != null && routine.owner_profile_id === targetProfileId) {
      return { own: false, label: `${nameOf(targetProfileId) ?? 'The athlete'}’s own routine` }
    }
    if (routine.subject_scope === 'group') {
      const team = known.find((a) => a.group_uuid === routine.subject_group_uuid)?.group_name
      return { own: true, label: `Watching ${team ?? 'your team'}` }
    }
    if (routine.subject_scope === 'athlete') {
      return { own: true, label: `Watching ${nameOf(routine.subject_profile_id) ?? 'an athlete'}` }
    }
    return { own: true, label: null }
  }, [athletes, targetProfileId])
}
